import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Printer, Lock, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLanguage, translations } from "../context/LanguageContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrintableCertificate = ({ cert, signatureData }) => {
  const completionDate = new Date(cert.completion_date).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric"
  });

  return (
    <div id="printable-certificate" className="certificate-print-area">
      <div className="w-[850px] mx-auto bg-white border-[3px] border-warm-800 relative" style={{ minHeight: '1050px' }}>
        {/* Decorative borders */}
        <div className="absolute inset-3 border-2 border-warm-600/30 pointer-events-none" />
        <div className="absolute inset-5 border border-warm-600/15 pointer-events-none" />
        <div className="absolute top-2 left-2 w-12 h-12 border-t-4 border-l-4 border-warm-700" />
        <div className="absolute top-2 right-2 w-12 h-12 border-t-4 border-r-4 border-warm-700" />
        <div className="absolute bottom-2 left-2 w-12 h-12 border-b-4 border-l-4 border-warm-700" />
        <div className="absolute bottom-2 right-2 w-12 h-12 border-b-4 border-r-4 border-warm-700" />

        <div className="relative px-14 py-10 flex flex-col h-full" style={{ minHeight: '1050px' }}>
          {/* Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-4 mb-3">
              <img src="/logo-new.png" alt="The Pillar Project Logo" className="w-14 h-14 object-contain" />
              <div>
                <h2 className="text-xl font-bold text-warm-800 tracking-wide" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                  THE PILLAR PROJECT CORPORATION
                </h2>
                <p className="text-xs text-gray-500 tracking-[0.12em]">
                  Baltimore, Maryland &bull; director@thepillarprojectbaltimore.org
                </p>
              </div>
            </div>
            <div className="w-40 h-[2px] bg-warm-700 mx-auto my-3" />
            <h1 className="text-3xl font-bold text-gray-900 tracking-wide uppercase" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', letterSpacing: '0.12em' }}>
              Certificate of Completion
            </h1>
          </div>

          {/* Body */}
          <div className="flex-1 flex flex-col justify-center">
            <p className="text-center text-gray-600 text-sm mb-3">This is to certify that</p>

            {/* Participant Name */}
            <h2 className="text-center text-3xl font-bold text-warm-800 mb-1 pb-2 border-b-2 border-warm-200 mx-auto px-10 inline-block" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              {cert.participant_name}
            </h2>

            {/* Case Number */}
            {cert.case_number && (
              <p className="text-center text-sm text-gray-500 mt-2">
                Case Number: <span className="font-mono font-semibold text-gray-700">{cert.case_number}</span>
              </p>
            )}

            <p className="text-center text-gray-600 text-sm mt-5 leading-relaxed max-w-lg mx-auto">
              has successfully completed all requirements of the
            </p>

            {/* Program Title */}
            <h3 className="text-center text-xl font-bold text-gray-900 mt-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              {cert.program_name}
            </h3>

            {/* Duration */}
            <p className="text-center text-sm text-gray-600 mt-1">
              Total: <span className="font-semibold text-gray-800">{cert.total_hours}</span>
            </p>

            {/* Completion Date */}
            <p className="text-center text-gray-600 text-sm mt-3">
              Completed on <span className="font-semibold text-gray-800">{completionDate}</span>
            </p>

            {/* Verification Statement */}
            <div className="mt-6 mx-auto max-w-xl border border-gray-200 rounded-lg p-4 bg-gray-50/50">
              <p className="text-xs text-gray-700 leading-relaxed text-center italic">
                I solemnly declare and affirm under penalties of perjury that the above information is true 
                and correct to the best of my knowledge. This certificate is issued by The Pillar Project 
                Corporation, a 501(c)(3) nonprofit organization, and may be presented to courts, the 
                Department of Human Services, child protective services, and other agencies as proof of 
                program completion.
              </p>
            </div>
          </div>

          {/* Signature & Footer Section */}
          <div className="mt-8">
            {/* Signature line */}
            <div className="flex justify-between items-end px-4 mb-4">
              <div className="text-center">
                <div className="w-56 border-b-2 border-gray-400 mb-1 h-14 flex items-end justify-center">
                  {signatureData ? (
                    <img src={signatureData} alt="Director signature" className="h-12 object-contain" />
                  ) : (
                    <span className="text-gray-800 italic text-lg pb-1" style={{ fontFamily: 'Georgia, serif' }}>
                      {cert.instructor_name}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-700 font-semibold">{cert.instructor_name}, {cert.instructor_title}</p>
                <p className="text-[10px] text-gray-500">The Pillar Project Corporation</p>
              </div>
              <div className="text-center">
                <div className="w-48 border-b-2 border-gray-400 mb-1 h-14 flex items-end justify-center">
                  <span className="text-gray-800 text-sm pb-1">{completionDate}</span>
                </div>
                <p className="text-xs text-gray-700 font-semibold">Date of Issuance</p>
              </div>
            </div>

            {/* Certificate ID */}
            <div className="border-t border-gray-300 pt-3 flex justify-between items-center">
              <div className="text-[10px] text-gray-400">
                <p>Certificate No: <span className="font-mono font-semibold">{cert.certificate_number}</span></p>
                <p>Issued: {new Date(cert.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
              </div>
              <div className="text-[10px] text-gray-400 text-right">
                <p>The Pillar Project Corporation</p>
                <p>Baltimore, Maryland &bull; 501(c)(3) Nonprofit</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CertificatePage = () => {
  const { language } = useLanguage();
  const t = translations[language].certificate;
  const [code, setCode] = useState("");
  const [certificate, setCertificate] = useState(null);
  const [signatureData, setSignatureData] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/certificates/signature`)
      .then(res => setSignatureData(res.data.signature_data || ""))
      .catch(() => {});
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setIsVerifying(true);
    setError("");
    try {
      const res = await axios.post(`${API}/certificates/verify`, { code: code.trim() });
      setCertificate(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || t.errorGeneric;
      setError(detail);
      toast.error(detail);
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePrint = () => window.print();

  if (certificate) {
    return (
      <div>
        <div className="print-hide bg-gray-50 border-b border-gray-200 py-4">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                {t.certificateReady}
              </h2>
              <p className="text-sm text-gray-500">{t.printInstructions}</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCertificate(null)} data-testid="cert-back-btn">
                {t.verifyAnother}
              </Button>
              <Button onClick={handlePrint} data-testid="cert-print-btn" className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
                <Printer className="w-4 h-4" />{t.printCertificate}
              </Button>
            </div>
          </div>
        </div>
        <div className="py-8 flex justify-center overflow-x-auto">
          <PrintableCertificate cert={certificate} signatureData={signatureData} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto text-center">
            <div className="w-16 h-16 bg-warm-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-8 h-8 text-warm-700" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight text-gray-900 mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }} data-testid="cert-page-title">
              {t.title}
            </h1>
            <p className="text-lg text-gray-600 mb-10">{t.subtitle}</p>

            <form onSubmit={handleVerify} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-left">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                <Lock className="w-4 h-4" />
                <span>{t.codeExplanation}</span>
              </div>
              <div className="mb-6">
                <Label htmlFor="cert-code" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.codeLabel}
                </Label>
                <Input id="cert-code" data-testid="cert-code-input" required
                  value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
                  className="rounded-xl border-gray-200 font-mono text-lg tracking-wider text-center"
                  placeholder="CERT-XXXXXX" autoComplete="off" />
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" data-testid="cert-error">
                  {error}
                </div>
              )}
              <Button type="submit" data-testid="cert-verify-btn"
                disabled={isVerifying || !code.trim()}
                className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-full py-6 gap-2">
                {isVerifying ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t.verifying}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t.viewCertificate}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <p className="text-sm text-gray-400 mt-6">{t.noCode}</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CertificatePage;
